// Fichier: src/pages/EvaluationForm.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  orderBy,
  query,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  Slider,
  Divider,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddIcon from '@mui/icons-material/Add';

// ─── Critères prédéfinis ─────────────────────────────────────────────────────
const DEFAULT_SKILLS = [
  { key: 'technique',       label: 'Technique' },
  { key: 'musicality',      label: 'Musicalité' },
  { key: 'freestyle',       label: 'Freestyle' },
  { key: 'memorization',    label: 'Mémorisation chorégraphique' },
  { key: 'stagePresence',   label: 'Présence scénique' },
  { key: 'confidence',      label: 'Confiance' },
  { key: 'energyIntention', label: 'Énergie / intention' },
  { key: 'learningSpeed',   label: "Rapidité d'apprentissage" },
  { key: 'precision',       label: 'Précision' },
  { key: 'endurance',       label: 'Endurance' },
  { key: 'versatility',     label: 'Polyvalence' },
  { key: 'discipline',      label: 'Discipline / régularité' },
  { key: 'personalWork',    label: 'Travail personnel' },
];

// Sélection rapide
const QUICK_GROUPS = [
  { label: 'Technique & précision', keys: ['technique', 'precision', 'endurance'] },
  { label: 'Artistique',            keys: ['musicality', 'freestyle', 'stagePresence', 'energyIntention'] },
  { label: 'Mental & régularité',   keys: ['confidence', 'discipline', 'personalWork', 'learningSpeed'] },
  { label: 'Tout sélectionner',     keys: DEFAULT_SKILLS.map((s) => s.key) },
];

const SCORE_COLOR = (score) => {
  if (score >= 8) return '#16a34a';
  if (score >= 5) return '#7C3AED';
  return '#dc2626';
};

// ─── Composant ───────────────────────────────────────────────────────────────
const EvaluationForm = () => {
  const { profileId, evalId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(evalId);

  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [profileName, setProfileName]   = useState('');

  // Champs principaux
  const [title, setTitle]               = useState('');
  const [date, setDate]                 = useState(new Date().toISOString().slice(0, 10));
  const [globalComment, setGlobalComment] = useState('');

  // Critères : fusion prédéfinis + custom depuis Firestore
  const [allSkills, setAllSkills]       = useState(DEFAULT_SKILLS);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [scores, setScores]             = useState({});
  const [comments, setComments]         = useState({});

  // Ajout d'un critère custom
  const [newCriteriaLabel, setNewCriteriaLabel] = useState('');
  const [addingCriteria, setAddingCriteria]     = useState(false);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // Nom de l'élève
        const profileSnap = await getDoc(doc(db, 'student_evolution_profiles', profileId));
        if (profileSnap.exists()) setProfileName(profileSnap.data().studentName || 'Élève');

        // Critères custom globaux
        const criteriaSnap = await getDocs(
          query(collection(db, 'evaluation_criteria'), orderBy('createdAt', 'asc'))
        );
        const customSkills = criteriaSnap.docs.map((d) => ({ key: d.id, label: d.data().label }));
        const merged = [
          ...DEFAULT_SKILLS,
          ...customSkills.filter((c) => !DEFAULT_SKILLS.find((d) => d.key === c.key)),
        ];
        setAllSkills(merged);

        // Si édition : charger l'évaluation
        if (isEditing) {
          const evalSnap = await getDoc(
            doc(db, 'student_evolution_profiles', profileId, 'evaluations', evalId)
          );
          if (evalSnap.exists()) {
            const data = evalSnap.data();
            const d = data.date?.toDate?.();
            if (d) setDate(d.toISOString().slice(0, 10));
            setTitle(data.title || '');
            setGlobalComment(data.globalComment || '');

            const criteria = data.criteria || [];

            // Ajouter les critères de cette éval qui ne seraient pas encore dans allSkills
            const extraKeys = criteria
              .filter((c) => !merged.find((s) => s.key === c.key))
              .map((c) => ({ key: c.key, label: c.label }));
            if (extraKeys.length) setAllSkills((prev) => [...prev, ...extraKeys]);

            setSelectedKeys(criteria.map((c) => c.key));
            const sc = {}, cm = {};
            criteria.forEach((c) => {
              sc[c.key] = c.score ?? 5;
              cm[c.key] = c.comment ?? '';
            });
            setScores(sc);
            setComments(cm);
          }
        }
      } catch (err) {
        console.error('Erreur init EvaluationForm:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [profileId, evalId, isEditing]);

  // ── Gestion critères ──────────────────────────────────────────────────────
  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      setScores((s) => ({ ...s, [key]: s[key] ?? 5 }));
      return [...prev, key];
    });
  };

  const applyQuickGroup = (keys) => {
    setSelectedKeys((prev) => {
      const merged = [...new Set([...prev, ...keys])];
      setScores((s) => {
        const next = { ...s };
        keys.forEach((k) => { if (next[k] === undefined) next[k] = 5; });
        return next;
      });
      return merged;
    });
  };

  // Créer un critère custom dans Firestore et l'ajouter à la liste
  const handleAddCriteria = async () => {
    const label = newCriteriaLabel.trim();
    if (!label) return;
    setAddingCriteria(true);
    try {
      const key = label
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');

      // Vérifier doublon
      if (allSkills.find((s) => s.key === key || s.label.toLowerCase() === label.toLowerCase())) {
        alert('Ce critère existe déjà.');
        setAddingCriteria(false);
        return;
      }

      await addDoc(collection(db, 'evaluation_criteria'), {
        key,
        label,
        createdAt: serverTimestamp(),
      });

      const newSkill = { key, label };
      setAllSkills((prev) => [...prev, newSkill]);
      // Sélectionner automatiquement le nouveau critère
      setSelectedKeys((prev) => [...prev, key]);
      setScores((prev) => ({ ...prev, [key]: 5 }));
      setNewCriteriaLabel('');
    } catch (err) {
      console.error('Erreur création critère:', err);
    } finally {
      setAddingCriteria(false);
    }
  };

  // ── Sauvegarde ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedKeys.length === 0) {
      alert('Sélectionne au moins un critère à évaluer.');
      return;
    }
    setSaving(true);
    try {
      const criteria = selectedKeys.map((key) => {
        const skill = allSkills.find((s) => s.key === key);
        return {
          key,
          label: skill?.label || key,
          score: scores[key] ?? 5,
          comment: comments[key]?.trim() || '',
        };
      });

      const payload = {
        title: title.trim(),
        date: Timestamp.fromDate(new Date(date)),
        globalComment: globalComment.trim(),
        criteria,
        updatedAt: serverTimestamp(),
      };

      const colRef = collection(db, 'student_evolution_profiles', profileId, 'evaluations');

      if (isEditing) {
        await updateDoc(doc(colRef, evalId), payload);
      } else {
        payload.createdAt = serverTimestamp();
        await addDoc(colRef, payload);
      }

      navigate(`/student-evolutions/${profileId}`);
    } catch (err) {
      console.error('Erreur sauvegarde évaluation:', err);
      alert('Erreur lors de la sauvegarde. Regarde la console.');
    } finally {
      setSaving(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  const defaultSkillKeys = DEFAULT_SKILLS.map((s) => s.key);
  const customSkills = allSkills.filter((s) => !defaultSkillKeys.includes(s.key));

  const averageScore =
    selectedKeys.length > 0
      ? (selectedKeys.reduce((sum, k) => sum + (scores[k] ?? 5), 0) / selectedKeys.length).toFixed(1)
      : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/student-evolutions/${profileId}`)}
        sx={{ mb: 2 }}
      >
        Retour au profil
      </Button>

      <Paper elevation={3} sx={{ p: 3 }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#7C3AED' }}>
              {isEditing ? "Modifier l'évaluation" : 'Nouvelle évaluation'}
            </Typography>
            <Typography color="text.secondary">{profileName}</Typography>
          </Box>
          {averageScore && (
            <Box sx={{ textAlign: 'center', bgcolor: '#F5F3FF', borderRadius: 2, px: 3, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Moyenne</Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: SCORE_COLOR(Number(averageScore)) }}>
                {averageScore}
                <Typography component="span" variant="h6" color="text.secondary">/10</Typography>
              </Typography>
            </Box>
          )}
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>

            {/* Titre + Date */}
            <Grid item xs={12} md={8}>
              <TextField
                label="Titre de l'évaluation"
                placeholder="Ex : Bilan octobre, Séance du 10 juin, Mi-saison..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Section critères */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mt: 2, mb: 0.5 }}>
                1. Critères à évaluer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sélectionne les critères pertinents pour cette évaluation.
              </Typography>

              {/* Raccourcis */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {QUICK_GROUPS.map((g) => (
                  <Chip
                    key={g.label}
                    label={g.label}
                    onClick={() => applyQuickGroup(g.keys)}
                    variant="outlined"
                    size="small"
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#EDE9FE' } }}
                  />
                ))}
                {selectedKeys.length > 0 && (
                  <Chip
                    label="Tout effacer"
                    onClick={() => setSelectedKeys([])}
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                  />
                )}
              </Box>

              {/* Critères prédéfinis */}
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Critères prédéfinis
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {DEFAULT_SKILLS.map((skill) => {
                  const selected = selectedKeys.includes(skill.key);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={skill.key}>
                      <Box
                        onClick={() => toggleKey(skill.key)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          p: 1.2, borderRadius: 2, border: '1px solid',
                          borderColor: selected ? '#7C3AED' : 'grey.200',
                          bgcolor: selected ? '#F5F3FF' : 'transparent',
                          cursor: 'pointer', transition: 'all 0.15s',
                          '&:hover': { borderColor: '#7C3AED', bgcolor: '#F5F3FF' },
                        }}
                      >
                        {selected
                          ? <CheckBoxIcon sx={{ color: '#7C3AED', fontSize: 20 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                        }
                        <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
                          {skill.label}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Critères custom existants */}
              {customSkills.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Critères personnalisés
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {customSkills.map((skill) => {
                      const selected = selectedKeys.includes(skill.key);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={skill.key}>
                          <Box
                            onClick={() => toggleKey(skill.key)}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1,
                              p: 1.2, borderRadius: 2, border: '1px solid',
                              borderColor: selected ? '#7C3AED' : 'grey.200',
                              bgcolor: selected ? '#F5F3FF' : 'transparent',
                              cursor: 'pointer', transition: 'all 0.15s',
                              '&:hover': { borderColor: '#7C3AED', bgcolor: '#F5F3FF' },
                            }}
                          >
                            {selected
                              ? <CheckBoxIcon sx={{ color: '#7C3AED', fontSize: 20 }} />
                              : <CheckBoxOutlineBlankIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                            }
                            <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
                              {skill.label}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}

              {/* Ajouter un critère custom */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  size="small"
                  placeholder="Nouveau critère (ex: Acrobaties, Portés...)"
                  value={newCriteriaLabel}
                  onChange={(e) => setNewCriteriaLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCriteria(); } }}
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><AddIcon sx={{ color: 'grey.400', fontSize: 18 }} /></InputAdornment>,
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddCriteria}
                  disabled={!newCriteriaLabel.trim() || addingCriteria}
                  sx={{ color: '#7C3AED', borderColor: '#7C3AED', whiteSpace: 'nowrap' }}
                >
                  {addingCriteria ? <CircularProgress size={16} /> : 'Ajouter critère'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Les critères ajoutés sont sauvegardés et réutilisables pour toutes les évaluations.
              </Typography>
            </Grid>

            {/* Notes par critère */}
            {selectedKeys.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                  2. Notes & commentaires ({selectedKeys.length} critère{selectedKeys.length > 1 ? 's' : ''})
                </Typography>
                <Grid container spacing={2}>
                  {selectedKeys.map((key) => {
                    const skill = allSkills.find((s) => s.key === key);
                    const score = scores[key] ?? 5;
                    return (
                      <Grid item xs={12} md={6} key={key}>
                        <Paper variant="outlined" sx={{ p: 2, borderColor: '#7C3AED22', borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>{skill?.label}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: SCORE_COLOR(score), minWidth: 44, textAlign: 'right' }}>
                              {score}/10
                            </Typography>
                          </Box>
                          <Slider
                            value={score}
                            min={0} max={10} step={1} marks
                            onChange={(_, v) => setScores((prev) => ({ ...prev, [key]: v }))}
                            sx={{ color: SCORE_COLOR(score) }}
                          />
                          <TextField
                            label="Commentaire (optionnel)"
                            value={comments[key] || ''}
                            onChange={(e) => setComments((prev) => ({ ...prev, [key]: e.target.value }))}
                            fullWidth size="small" multiline rows={2} sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            )}

            {/* Commentaire global */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                3. Commentaire global
              </Typography>
              <TextField
                label="Synthèse, points clés, axes de travail..."
                value={globalComment}
                onChange={(e) => setGlobalComment(e.target.value)}
                fullWidth multiline rows={5}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/student-evolutions/${profileId}`)}
              sx={{ color: '#7C3AED', borderColor: '#7C3AED' }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' }, minWidth: 160 }}
            >
              {saving
                ? <CircularProgress size={20} sx={{ color: 'white' }} />
                : isEditing ? 'Enregistrer les modifications' : "Créer l'évaluation"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default EvaluationForm;